// Pure helpers for the customer-performance widgets (Tiered / Non-Tiered), which
// share an identical response shape. Kept free of React/redux so the per-period
// lookups and the "% of jobs with an issue" computation can be unit tested in
// isolation.
//
// The widget's backend response (result[0]) holds one object per issue row
// (uncoveredLabor / uncoveredMaterialStockout / qualities / shortages), each
// with per-period counts { wtd, qtd, ytd } (+ reason/*FromDate/toDate for
// drill-down). It also carries flat per-period job totals
// (weekJobCount / qtdJobCount / ytdJobCount) and an OVERALL percent per period
// (weekPercentJobsWithIssue / ...). There is no per-issue percent, so the
// widget's per-row "%" is computed here as jobsWithIssue / totalJobs.
//
// Note the period-key inconsistency in the backend payload: issue rows use
// `wtd`, while the flat totals use a `week` prefix (`weekJobCount`). PERIOD_TOTAL
// maps the widget's period ids to those flat field names.

export const ISSUE_ROW_KEYS = [
  "uncoveredLabor",
  "uncoveredMaterialStockout",
  "qualities",
  "shortages",
];

// Widget period id -> flat "total jobs" field name in the response.
const PERIOD_TOTAL = {
  wtd: "weekJobCount",
  qtd: "qtdJobCount",
  ytd: "ytdJobCount",
};

// One issue-row count for a period (e.g. qualities.wtd), i.e. "Jobs with Issue".
// Defaults to 0 so the widget renders safely before/without those fields.
export const getCell = (data, key, period) =>
  data && data[key] && data[key][period] != null
    ? Number(data[key][period]) || 0
    : 0;

// Total number of jobs for a period ("Total Jobs"), read from the flat
// {week|qtd|ytd}JobCount fields, defaulting to 0.
export const getTotalJobs = (data, period) => {
  const field = PERIOD_TOTAL[period];
  return data && field && data[field] != null ? Number(data[field]) || 0 : 0;
};

// Per-issue "%" for a period = jobsWithIssue / totalJobs, rounded to 1 decimal.
// Returns 0 when there are no jobs (avoids divide-by-zero). The backend only
// returns an overall per-period percent, so this rate is computed client-side.
export const getIssuePercent = (data, key, period) => {
  const total = getTotalJobs(data, period);
  if (!total) return 0;
  return Math.round((getCell(data, key, period) / total) * 1000) / 10;
};
